<template>
  <div class="sidebar-category-wrapper" :class="{ 'is-rail': collapsed }">
    <div class="menu-item-container" @mouseenter="reportHover" @mouseleave="$emit('item-leave')">
      <div v-if="routeName && !isConnectedDataDropdown" class="menu-route-item" :class="{ border: isConnectedData }">
        <router-link :id="`${routeName}-link`" :to="{ name: routeName }" :params="routeParams || {}" class="menu-item-content" @click.native="linkClick()">
          <span class="material-symbols-outlined menu-item-icon"> {{ icon }} </span>
          <span class="menu-item-text">{{ label }}</span>
          <span v-if="badge" class="badge">{{ badge }}</span>
          <AlertBadge :count="alertCount" />
        </router-link>

        <SidebarIntegrations v-if="isConnectedData" />
      </div>

      <!-- menu category header, and Connected data on mobile, which collapses instead of showing its card inline -->
      <div
        v-else
        class="menu-category-item"
        :class="{ 'has-active-child': hasActiveChild, 'is-expanded': isExpanded && !collapsed }"
        role="button"
        tabindex="0"
        :aria-expanded="String(isExpanded)"
        @click="handleMenuExpand"
        @keydown.enter="handleMenuExpand"
        @keydown.space.prevent="handleMenuExpand"
      >
        <div class="menu-item-content">
          <span class="material-symbols-outlined menu-item-icon"> {{ icon }} </span>
          <span class="menu-item-text">{{ label }}</span>
          <span v-if="badge" class="badge">{{ badge }}</span>
          <AlertBadge :count="alertCount" />
        </div>
        <span class="material-icons-outlined chevron" :class="{ flipped: isExpanded }">expand_more</span>
      </div>
    </div>

    <!-- dropdown container with child links -->
    <transition v-if="menuItems" name="slide">
      <div v-if="isExpanded" class="sidebar-dropdown-content-container">
        <SidebarCategoryChild
          v-for="item in menuItems"
          :key="item.label"
          :label="item.label"
          :routeName="item.routeName"
          :externalLink="item.externalLink"
          :productName="item.productName"
          @link-clicked="linkClick()"
        />
      </div>
    </transition>

    <transition v-if="isConnectedDataDropdown" name="slide">
      <div v-if="isExpanded" class="sidebar-dropdown-content-container">
        <SidebarIntegrations />
      </div>
    </transition>
  </div>
</template>

<script>
import SidebarCategoryChild from '@/components/layout/SidebarCategoryChild.vue'
import SidebarIntegrations from '@/components/layout/SidebarIntegrations.vue'
import AlertBadge from '@/components/AlertBadge.vue'

export default {
  name: 'sidebar-category',
  components: {
    SidebarCategoryChild,
    SidebarIntegrations,
    AlertBadge
  },
  props: {
    category: {
      type: Object
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    mobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isExpanded: !!this.category.expanded
    }
  },
  computed: {
    isConnectedData() {
      return this.routeName === 'integrations'
    },
    isConnectedDataDropdown() {
      return this.isConnectedData && this.mobile
    },
    menuItems() {
      return this.category.menuItems
    },
    label() {
      return this.category.label
    },
    icon() {
      return this.category.icon
    },
    badge() {
      return this.category.badge
    },
    alertCount() {
      return this.category.alertCount
    },
    routeName() {
      return this.category.routeName
    },
    routeParams() {
      return this.category.routeParams
    },
    hasActiveChild() {
      if (!this.menuItems?.length) return false
      const current = this.$route?.name
      return this.menuItems.some(item => item.routeName === current)
    }
  },
  methods: {
    linkClick() {
      this.$emit('close-mobile-sidebar')
    },
    reportHover(event) {
      this.$emit('item-hover', {
        label: this.label,
        flyoutLabel: this.category.flyoutLabel || this.label,
        el: event.currentTarget,
        menuItems: this.menuItems
      })
    },
    handleMenuExpand() {
      if (this.collapsed) return this.openFirstChild()
      this.isExpanded = !this.isExpanded
    },
    openFirstChild() {
      const first = this.menuItems?.[0]
      if (!first) return
      if (first.externalLink) return window.open(first.externalLink, '_blank')
      if (first.routeName && first.routeName !== this.$route?.name) this.$router.push({ name: first.routeName })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/swoop/_variables.scss';

$sidebar-divider-color: rgba(255, 255, 255, 0.1);
$sidebar-ink: rgba(255, 255, 255, 0.8);
$sidebar-ink-faint: rgba(255, 255, 255, 0.55);
$sidebar-hover: rgba(255, 255, 255, 0.07);
$sidebar-selected-text: var(--color-secondary-200);
$sidebar-selected-fill: color-mix(in srgb, var(--color-secondary-500) 26%, transparent);

.sidebar-category-wrapper {
  display: flex;
  flex-direction: column;
  cursor: pointer;
  margin: 2px auto;
  width: 100%;
  outline: none;
  user-select: none;

  .menu-item-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .menu-route-item,
    .menu-category-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      font-size: 15px;
      font-weight: 400;
      color: $sidebar-ink;
      width: 100%;
      cursor: pointer;

      .menu-item-content {
        display: flex;
        align-items: center;
        text-decoration: none;
        height: 44px;
        padding: 0 14px;
        width: 100%;
        gap: 12px;
        color: $sidebar-ink;
        border-radius: 8px;
        transition: background-color 0.12s ease, color 0.12s ease;

        &:focus-visible {
          outline: 1px solid var(--color-white);
          outline-offset: -1px;
        }

        .material-symbols-outlined {
          color: inherit;
          font-size: 22px;
          flex-shrink: 0;
          font-variation-settings: 'FILL' 0;
        }

        .menu-item-text {
          color: inherit;
          font-size: 15px;
          font-weight: inherit;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .badge {
          background-color: rgba(255, 255, 255, 0.86);
          color: var(--color-primary-600);
          font-size: 10px;
          font-weight: 700;
          line-height: 1;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 4px 8px;
          border-radius: 999px;
          flex-shrink: 0;
        }
      }
    }

    .menu-route-item {
      .menu-item-content {
        &:hover {
          text-decoration: none;
          background-color: $sidebar-hover;
          color: var(--color-white);

          .material-symbols-outlined,
          .menu-item-text {
            color: var(--color-white);
          }

          .material-symbols-outlined {
            font-variation-settings: 'FILL' 1;
          }
        }

        &.router-link-active,
        &.router-link-active:hover {
          background-color: $sidebar-selected-fill;
          box-shadow: inset 3px 0 0 var(--color-secondary-500);
          color: $sidebar-selected-text;
          font-weight: 700;

          .material-symbols-outlined,
          .menu-item-text {
            color: $sidebar-selected-text;
          }

          .material-symbols-outlined {
            font-variation-settings: 'FILL' 1;
          }
        }
      }

      &.border {
        flex-direction: column;
        align-items: stretch;

        .menu-item-content {
          gap: 10px;
          padding: 0 12px;
          font-size: 14px;
          font-weight: 600;
          color: var(--color-white);

          .menu-item-text {
            font-size: 14px;
          }

          .material-symbols-outlined {
            color: $sidebar-selected-text;
          }
        }
      }
    }

    // The chevron sits outside .menu-item-content, so the highlight has to be painted on the whole row to enclose it.
    .menu-category-item {
      border-radius: 8px;
      transition: background-color 0.12s ease, color 0.12s ease;

      &:focus-visible {
        outline: 1px solid var(--color-white);
        outline-offset: -1px;
      }

      &:hover {
        background-color: $sidebar-hover;

        .menu-item-content,
        .menu-item-content .menu-item-text,
        .menu-item-content .material-symbols-outlined,
        .chevron {
          color: var(--color-white);
        }

        .menu-item-content .material-symbols-outlined {
          font-variation-settings: 'FILL' 1;
        }
      }

      // A collapsed group carries the selection of whichever child is active; once open the child shows it instead.
      &.has-active-child:not(.is-expanded) {
        background-color: $sidebar-selected-fill;
        box-shadow: inset 3px 0 0 var(--color-secondary-500);
        font-weight: 700;

        .menu-item-content,
        .menu-item-content .menu-item-text,
        .menu-item-content .material-symbols-outlined,
        .chevron {
          color: $sidebar-selected-text;
        }

        .menu-item-content .material-symbols-outlined {
          font-variation-settings: 'FILL' 1;
        }
      }

      .chevron {
        color: $sidebar-ink-faint;
        font-size: 20px;
        margin-right: 8px;
        transition: transform 0.16s ease;

        &.flipped {
          transform: rotate(180deg);
        }
      }
    }
  }

  .sidebar-dropdown-content-container {
    overflow: hidden;
    margin: 2px 0 6px;
    height: fit-content;
    transition: max-height 0.2s ease-in-out;

    &.slide-enter-active,
    &.slide-leave-active {
      max-height: 500px;
    }

    &.slide-enter,
    &.slide-leave-to {
      max-height: 0;
    }
  }

  // The rail shows selection as a fill only; the edge bar has nothing to sit against at 72px.
  // Kept here rather than in AppSidebar so it outranks the active rule in this same scoped block.
  &.is-rail .menu-item-container .menu-route-item .menu-item-content.router-link-active,
  &.is-rail .menu-item-container .menu-category-item.has-active-child:not(.is-expanded) {
    box-shadow: none;
  }
}
</style>
